<template>
  <div class="view-profile">
    <h1 class="mt-0">{{ $t("headings.profile") }}</h1>
    <form @submit.prevent="">
      <div>
        <label class="label">{{ $t("labels.email") }}</label>
        <div>{{ data.email }}</div>
        <hr />
      </div>
      <div>
        <label class="label">{{ $t("labels.password") }}</label>
        <a href="#" @click.prevent="sendPasswordResetRequest()">
          {{ $t("actions.changePassword") }}
        </a>
        <hr />
      </div>

      <div class="view-profile__user">
        <div class="w-full view-profile__profile">
          <div class="column">
            <input-text
              v-model:value="data.firstname"
              :label="$t('labels.firstname')"
            />
            <input-text
              v-model:value="data.lastname"
              :label="$t('labels.lastname')"
            />
          </div>
        </div>
        <div>
          <!-- PROFILE IMAGE WILL BE HERE SOON -->
          <user-avatar :user="user" class="view-profile__avatar" />
        </div>
      </div>

      <hr />

      <button
        class="button"
        type="button"
        :disabled="anythingChanged == false"
        @click="saveProfile"
      >
        {{ $t("labels.save") }}
      </button>
    </form>

    <div class="mt-auto">
      <hr />
      <button
        class="button button_secondary"
        type="button"
        @click="deleteAccount()"
      >
        {{ $t("actions.deleteAccount") }}
      </button>
    </div>

    <!--<pre>{{ user }}</pre>-->
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import * as API from "@/api/API";

import { useUser } from "@/api/user.store";
import InputText from "@core/input/InputText.vue";
import UserAvatar from "../common/UserAvatar.vue";
import { showError, showMessage, showPrompt } from "@core/ui/alert/alert";

const router = useRouter();
const { t } = useI18n();
const { user, updateUser, logout } = useUser();

const data = reactive({
  firstname: "",
  lastname: "",
  email: ""
});

const anythingChanged = computed(() => {
  return (
    data.firstname !== user.value?.firstname ||
    data.lastname !== user.value?.lastname ||
    data.email !== user.value?.email
  );
});

watch(user, (value) => {
  if (value == null) return;
  data.firstname = value.firstname;
  data.lastname = value.lastname;
  data.email = value.email;
}, { immediate: true, deep: true });
  
function saveProfile() {
  if (!user.value) {
    showError("No user found");
    return;
  }
  updateUser({
    id: user.value.id,
    firstname: data.firstname,
    lastname: data.lastname,
  });
}

async function sendPasswordResetRequest() {
  if (!user.value) {
    showError("No user found");
    return;
  }
  try {
    await API.post("/request_password_reset_email/", {
      data: {
        email: user.value.email
      },
      sendAuthToken: false
    });
    showMessage(t("messages.passwordResetEmailSent"));
  } catch (e) {
    const error = e as Error;
    console.log("error", error);
    showError(error);
  }
}

async function deleteAccount() {
  if (!user.value) {
    showError("No user found");
    return;
  }
  const deleteIt = await showPrompt<false | { confirmDelete: string }>({
    text: t("questions.confirmDeleteAccount"),
    content: [
      {
        type: "input",
        label: t("labels.email"),
        name: "confirmDelete",
        value: "",
        required: true
      }
    ],
    confirmText: t("actions.deleteAccount"),
  });
  if (!deleteIt) return;
  if (deleteIt.confirmDelete == null) return;
  if (deleteIt.confirmDelete !== user.value.email) {
    showError(t("errors.notAccountsEmail"));
    return;
  }

  // Request account deletion
  // This sends an email with a verification code to the user.
  try {
    await API.post("/v1/user/request-delete");
  } catch (e) {
    const error = e as Error;
    console.log("error", error);
    showError(error);
    return;
  }

  // Check verification code
  const verificationCode = await showPrompt<false | { code: string }>({
    text: t("questions.deleteAccountVerificationCode"),
    content: [
      {
        type: "input",
        label: t("labels.verificationCode"),
        name: "code",
        value: "",
        required: true
      }
    ],
    confirmText: t("actions.deleteAccount"),
  });

  if (!verificationCode) return;

  try {
    const body = await API.destroy("/v1/user", {
      data: {
        code: verificationCode.code
      }
    });
    console.log(body);
    logout();
    router.push("/");
  } catch(e) {
    const error = e as Error;
    console.log("error", error);
    showError(error);
  }
}
</script>

<style lang="scss">
.view-profile {
  min-height: 100%;
  display: flex;
  flex-direction: column;

  &__user {
    display: flex;
    gap: 1rem;
  }

  &__profile,
  &__address {
    max-width: 36rem;
  }

  &__avatar {
    font-size: 2rem;
  }
}
</style>
